/**
 * GridAmbience (BL-286) — the board's LIVE under-layer.
 *
 * The baked grid (GridRenderer's cached line canvas) gives the surface its
 * material and depth; this gives it a pulse. The brief is a high-end command
 * interface, not an arcade backdrop, so the whole layer is built around one
 * rule: **the board should look like a complex network is running underneath
 * it, never like the background is asking for attention.**
 *
 * That rule is what fixes the budget, and the budget is deliberately tiny:
 *
 *   - 2 conduit pulses drifting along sector lines (a full traverse takes 14-19
 *     SECONDS — slow enough that the eye reads "something is flowing", never
 *     "something is moving");
 *   - up to 4 junction nodes breathing at ~0.05 Hz (an 18-27 s cycle) at alphas
 *     so low they only register in peripheral vision;
 *   - one diagnostic sweep every ~29 s, crossing over ~4.2 s, which brightens
 *     the lattice it passes over by at most ~19% and touches nothing else.
 *
 * BL-287 halved this budget again on owner review: the board should still live,
 * but "atmosphärische Aktivität, nicht Gameplay-Readout" — fewer things at once,
 * each slower and rarer.
 *
 * Per frame that is ~8 strokes, ~6 cached sprite blits, and — for the seventh of
 * the time a sweep is running — five clipped blits of an already-baked canvas
 * that together fill the band area once. No canvas is created, no gradient is
 * rebuilt, no array grows: **nothing is allocated per frame**. Every
 * position is a pure function of the wall clock (plus a deterministic hash for
 * variety), so the layer is frame-rate independent by construction: at 30 fps
 * it plays at exactly the same speed, just sampled half as often, and it can
 * never drift, pile up, or "catch up" after a stall.
 *
 * It self-gates on `arenaMotionScale()`: the perf governor fades it out under
 * load before it touches anything the player is actually reading, and reduced
 * motion removes it entirely. Nothing here is ever information — losing all of
 * it costs the player nothing.
 */

import { arenaMotionScale } from "../engine/MotionSettings.ts";
import { arenaDensityBias } from "../engine/PerfGovernor.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { BOARD } from "./designTokens.ts";

/**
 * Deterministic 0..1 hash (the MainframeRenderer grammar — no RNG in render).
 *
 * NOTE the `+ 1.61803` offset: `Math.sin(0)` is 0, so an un-offset hash returns
 * exactly 0 at the origin AND for every multiple of it. A seed of 0 therefore
 * collapsed every choice derived from it onto the same answer, which is how the
 * first conduit pulse of a session ended up pinned to the board's left edge for
 * its whole first traverse. Callers must also OFFSET rather than SCALE a shared
 * seed for their second and third draws, for the same reason.
 */
function hash01(n: number): number {
  const s = Math.sin((n + 1.61803) * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/** Grid-accent ink — a desaturated teal, held at 188° so it is separated from
 *  both gameplay greens by HUE and not merely by brightness (see `BOARD` in
 *  designTokens). Fixed rather than epoch-tinted, so a late act cannot quietly
 *  turn the backdrop loud. */
const ACCENT = BOARD.accent;
/** The brighter head of a travelling pulse. */
const ACCENT_HOT = BOARD.accentHot;

/** Every Nth line is a sector boundary — the lattice the ambience runs on. */
export const SECTOR_STEP = 4;

/**
 * Concurrent conduit pulses. BL-287 cut 3 → 2 and slowed every period:
 * the owner asked for the board to keep living but to do it *more quietly*
 * — "sehr langsame, seltene Impulse … keine hektische oder auffällige Bewegung".
 * Two pulses on a 14-19 s traverse means the board is usually showing exactly
 * one thing, which is the difference between atmosphere and traffic.
 */
const PULSE_COUNT = 2;
/** Base traverse time (ms). Each pulse offsets from this — no two in step. */
const PULSE_PERIOD_BASE = 14000;
const PULSE_PERIOD_SPREAD = 5000;
/** Trailing segments behind the head (the taper that makes it read as motion). */
const PULSE_TRAIL = 4;
/**
 * Peak alpha of a pulse. A pulse always rides an existing sector line, so this
 * is a RELATIVE lift, not an absolute brightness: at 0.13 the segment under the
 * head runs ~40% brighter than the line it is travelling on — readable as a
 * light moving along a conduit, and still an order of magnitude below anything
 * in the entity layer. 0.10 was tried first and read as line noise; 0.16 read as
 * traffic, which BL-287 pulled back from.
 */
const PULSE_ALPHA = 0.13;

/** Slow-breathing junction nodes. BL-287 cut 6 → 4 ("weniger gleichzeitig
 *  aktive Nodes") — and because each spends most of its cycle dark, four means
 *  one or two are faintly lit at any moment, not four. */
const NODE_MAX = 4;
/** One breath every 18 s at the fastest — a third slower than BL-286's 12.5 s,
 *  and far under anything the eye tracks as pulsing. */
const NODE_PERIOD_BASE = 18000;

/** Sweep cadence + crossing time (ms). BL-287 stretched the gap 17 s → 29 s and
 *  slowed the crossing, so the sweep is a rare event rather than a rhythm.
 *  Exported so the guard that asserts "rare" derives its sampling window from
 *  the real cadence instead of hardcoding one that a retune silently invalidates. */
export const SWEEP_PERIOD = 29000;
const SWEEP_TRAVEL = 4200;
/** How much brighter the lattice burns inside the band at the sweep's core.
 *  BL-287: 0.28 → 0.19 — still readable as a scan, no longer the loudest thing
 *  the board does. */
const SWEEP_ALPHA = 0.19;
/** Band width in cells, and the slices its soft profile is approximated with. */
const SWEEP_BAND_CELLS = 6;
const SWEEP_SLICES = 5;

/**
 * BL-287 / AC-13 — the layer's three sustained cadences, exported so the
 * photosensitivity bound is asserted against the REAL numbers instead of a
 * comment. `feel-direction.md` caps sustained oscillation at 1 Hz; every entry
 * here is a full period in ms, so the guard is simply "all >= 1000".
 */
export const AMBIENCE_CADENCE_MS = Object.freeze({
  /** Fastest possible pulse traverse (i = 0). */
  pulse: PULSE_PERIOD_BASE,
  /** Fastest possible node breath (the hash only ever adds). */
  node: NODE_PERIOD_BASE,
  /** Sweep repeat. */
  sweep: SWEEP_PERIOD,
});

interface Node {
  x: number;
  y: number;
  period: number;
  phase: number;
  /** Peak alpha — varied per node so the set never reads as a repeating array. */
  peak: number;
}

export class GridAmbience {
  private readonly widthPx: number;
  private readonly heightPx: number;
  private readonly cell: number;
  private readonly nodes: Node[] = [];
  /** INTERIOR sector lines a pulse may run on, carrying the SAME half-pixel
   *  offset the baked lattice is stroked at — without it a pulse travels half a
   *  pixel beside its own conduit and reads as a smudge next to the line rather
   *  than a light inside it. The board's outermost lines are excluded: a pulse
   *  there is half-clipped by the canvas edge and overlaps the spawn/mainframe
   *  furniture, so it reads as a glitch, not as traffic. */
  private readonly vLanes: number[] = [];
  private readonly hLanes: number[] = [];

  constructor(widthPx: number, heightPx: number, cellSize: number) {
    this.widthPx = widthPx;
    this.heightPx = heightPx;
    this.cell = cellSize;

    const step = cellSize * SECTOR_STEP;
    for (let x = step; x < widthPx; x += step) this.vLanes.push(x + 0.5);
    for (let y = step; y < heightPx; y += step) this.hLanes.push(y + 0.5);
    // Degenerate boards (smaller than one sector) still need a lane to pick.
    if (this.vLanes.length === 0) this.vLanes.push(widthPx / 2);
    if (this.hLanes.length === 0) this.hLanes.push(heightPx / 2);

    // Pick the breathing junctions once, deterministically, from the sector
    // lattice — spread by a hash so they never form a visible pattern.
    const cols = Math.floor(widthPx / cellSize);
    const rows = Math.floor(heightPx / cellSize);
    for (let row = SECTOR_STEP; row < rows && this.nodes.length < NODE_MAX; row += SECTOR_STEP) {
      for (let col = SECTOR_STEP; col < cols && this.nodes.length < NODE_MAX; col += SECTOR_STEP) {
        const h = hash01(col * 3.17 + row * 7.71);
        if (h < 0.72) continue;
        this.nodes.push({
          x: col * cellSize,
          y: row * cellSize,
          period: NODE_PERIOD_BASE + hash01(col * 1.3 + row * 2.9) * 9000,
          phase: hash01(col * 5.1 + row * 1.7),
          peak: 0.05 + hash01(col * 9.3 + row * 4.1) * 0.045,
        });
      }
    }
  }

  /**
   * Draw the live layer. `time` is a wall clock in ms (`performance.now()`);
   * everything is derived from it, so the caller owes no state.
   *
   * `surface` is the caller's BAKED grid-line canvas. The sweep re-blits it
   * additively through a moving clip, which is what makes the scan read as the
   * system lighting up its own lattice — see {@link renderSweep}. Omit it and
   * the sweep simply doesn't run; the rest of the layer is unaffected.
   */
  render(
    ctx: CanvasRenderingContext2D,
    time: number,
    surface?: HTMLCanvasElement | null,
  ): void {
    const m = arenaMotionScale();
    // Accessibility hard-kill. NOTE this is NOT the production load path: for
    // real users `motionScale()` is pinned to 1 and the governor clamps at
    // MOTION_HARD_FLOOR = 0.6, so `arenaMotionScale()` never reaches 0 in the
    // shipped game. Only a test can take this branch — which is exactly why it
    // must not be the ONLY thing standing between a loaded frame and this layer.
    if (m <= 0) return;
    // The real one. `arenaDensityBias()` is the governor's rung-3 signal and is
    // 0 until it is genuinely under measured load; when it fires, the board is
    // the first thing that should go, because nothing here is ever information
    // (the render signature cannot even see game state). This is the rung the
    // layer was missing: without it the sweep's clipped full-canvas blits kept
    // running at every degrade level, on the phone the ladder exists for.
    if (arenaDensityBias() > 0) return;

    const prevOp = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "lighter";
    this.renderNodes(ctx, time, m);
    this.renderPulses(ctx, time, m);
    if (surface) this.renderSweep(ctx, time, m, surface);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = prevOp;
  }

  /** Junction nodes: a barely-there breath at a handful of sector crossings. */
  private renderNodes(
    ctx: CanvasRenderingContext2D,
    time: number,
    m: number,
  ): void {
    for (const n of this.nodes) {
      const t = ((time / n.period + n.phase) % 1) * Math.PI * 2;
      // Sine mapped 0..1, then squared: most of the cycle sits DARK and the lit
      // part is a short swell — a breath, not a blink.
      const swell = (Math.sin(t) + 1) * 0.5;
      const a = n.peak * swell * swell * m;
      if (a < 0.012) continue;
      // A TIGHT halo around a hard 2 px point: a node has to read as a precise
      // light, not a soft smudge — a wide low-alpha blob looks like dirt on the
      // lens, which is the opposite of expensive.
      glowCache.draw(ctx, ACCENT, this.cell * 0.11, n.x, n.y, a);
      ctx.globalAlpha = Math.min(1, a * 3.2);
      ctx.fillStyle = ACCENT_HOT;
      ctx.fillRect(n.x - 1, n.y - 1, 2, 2);
    }
  }

  /**
   * Conduit pulses: a tapered head drifting the full length of a sector line.
   * The lane is re-picked per traverse, so the traffic never repeats a route
   * twice running, and the whole thing is 4 strokes + 1 cached blit each.
   */
  private renderPulses(
    ctx: CanvasRenderingContext2D,
    time: number,
    m: number,
  ): void {
    for (let i = 0; i < PULSE_COUNT; i++) {
      const period = PULSE_PERIOD_BASE + i * PULSE_PERIOD_SPREAD;
      const cycle = Math.floor(time / period);
      const t = (time % period) / period;
      const seed = cycle * 3.7 + i * 11.3;

      // Fade in over the first 12% and out over the last 25% so a pulse never
      // pops into or out of existence at a board edge.
      const edge = t < 0.12 ? t / 0.12 : t > 0.75 ? (1 - t) / 0.25 : 1;
      const alpha = PULSE_ALPHA * edge * m;
      if (alpha < 0.012) continue;

      // Draws are OFFSET from the shared seed, never scaled by it: scaling
      // makes every draw degenerate wherever the seed is 0 (see hash01).
      const vertical = hash01(seed) < 0.42;
      const lanes = vertical ? this.vLanes : this.hLanes;
      const lane = lanes[Math.floor(hash01(seed + 4.9) * lanes.length)]!;
      const dir = hash01(seed + 21.7) < 0.5 ? 1 : -1;
      const trailLen = this.cell * 1.9;
      let hx: number;
      let hy: number;
      let dx: number;
      let dy: number;
      if (vertical) {
        hx = lane;
        hy = dir > 0 ? t * this.heightPx : (1 - t) * this.heightPx;
        dx = 0;
        dy = -dir * trailLen;
      } else {
        hx = dir > 0 ? t * this.widthPx : (1 - t) * this.widthPx;
        hy = lane;
        dx = -dir * trailLen;
        dy = 0;
      }

      // Tapered trail — segments dim with distance behind the head.
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1;
      for (let s = 0; s < PULSE_TRAIL; s++) {
        const a0 = s / PULSE_TRAIL;
        const a1 = (s + 1) / PULSE_TRAIL;
        ctx.globalAlpha = alpha * (1 - a0);
        ctx.beginPath();
        ctx.moveTo(hx + dx * a0, hy + dy * a0);
        ctx.lineTo(hx + dx * a1, hy + dy * a1);
        ctx.stroke();
      }
      // The head: a tight halo plus a hard 2 px core. The halo alone read as a
      // soft blob drifting near the line — the core is what makes it a light.
      glowCache.draw(ctx, ACCENT_HOT, this.cell * 0.1, hx, hy, alpha * 1.8);
      ctx.globalAlpha = Math.min(1, alpha * 3.4);
      ctx.fillStyle = ACCENT_HOT;
      ctx.fillRect(hx - 1, hy - 1, 2, 2);
    }
  }

  /**
   * Diagnostic sweep: a wide band crossing the board every ~17 s.
   *
   * The first cut painted a soft gradient band into the VOID and was, correctly,
   * invisible: adding 5% of a dark green to near-black is nothing. What reads —
   * and what a command interface would actually do — is light the LATTICE. So
   * the band re-blits the already-baked grid canvas through a moving clip, on
   * the caller's `lighter` composite: the lines inside the band simply burn
   * brighter, then dim as it passes. A scan over the network, not a light leak.
   *
   * Cost is one clipped blit per slice, and a clip means each only rasterises
   * its own column, so the whole sweep fills the band area once — cheaper than
   * the gradient strip it replaced, and with no second canvas to allocate. The
   * slices approximate a soft brightness profile (a parabola across the band),
   * which is what keeps the leading and trailing edges from reading as walls.
   */
  private renderSweep(
    ctx: CanvasRenderingContext2D,
    time: number,
    m: number,
    surface: HTMLCanvasElement,
  ): void {
    const phase = time % SWEEP_PERIOD;
    if (phase > SWEEP_TRAVEL) return;
    const t = phase / SWEEP_TRAVEL;
    // Ease in and out at the board edges so it drifts through rather than
    // sliding on and off.
    const fade = Math.sin(t * Math.PI);
    const band = this.cell * SWEEP_BAND_CELLS;
    const left = t * (this.widthPx + band) - band;
    const sliceW = band / SWEEP_SLICES;

    for (let i = 0; i < SWEEP_SLICES; i++) {
      const x = left + i * sliceW;
      if (x + sliceW <= 0 || x >= this.widthPx) continue;
      const u = ((i + 0.5) / SWEEP_SLICES) * 2 - 1; // -1..1 across the band
      const a = SWEEP_ALPHA * fade * (1 - u * u) * m;
      if (a < 0.01) continue;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, 0, sliceW, this.heightPx);
      ctx.clip();
      ctx.globalAlpha = a;
      ctx.drawImage(surface, 0, 0);
      ctx.restore();
    }
  }
}
